import type { AgentPart, FilePart, SkillPart } from "@openanalyst-ai/sdk/v2"

export type HighlightSegment = { text: string; type?: "file" | "agent" | "skill" }

/** Split a user message's text into plain and mention-highlighted runs, using the
 *  `source` spans the file / @agent / @skill parts carry. Kept in its own module —
 *  free of Solid, motion and the timeline's module-level side effects — so it stays
 *  directly testable. */
export function highlightSegments(props: {
  text: string
  references: FilePart[]
  agents: AgentPart[]
  skills: SkillPart[]
}): HighlightSegment[] {
  const text = props.text

  const allRefs: { start: number; end: number; type: "file" | "agent" | "skill" }[] = [
    ...props.references
      .filter((r) => r.source?.text?.start !== undefined && r.source?.text?.end !== undefined)
      .map((r) => ({ start: r.source!.text!.start, end: r.source!.text!.end, type: "file" as const })),
    ...props.agents
      .filter((a) => a.source?.start !== undefined && a.source?.end !== undefined)
      .map((a) => ({ start: a.source!.start, end: a.source!.end, type: "agent" as const })),
    ...props.skills
      .filter((s) => s.source?.start !== undefined && s.source?.end !== undefined)
      .map((s) => ({ start: s.source!.start, end: s.source!.end, type: "skill" as const })),
  ].sort((a, b) => a.start - b.start)

  const result: HighlightSegment[] = []
  let lastIndex = 0

  for (const ref of allRefs) {
    if (ref.start < lastIndex) continue

    if (ref.start > lastIndex) {
      result.push({ text: text.slice(lastIndex, ref.start) })
    }

    result.push({ text: text.slice(ref.start, ref.end), type: ref.type })
    lastIndex = ref.end
  }

  if (lastIndex < text.length) {
    result.push({ text: text.slice(lastIndex) })
  }

  return result
}
